import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useContext, useEffect, useCallback, useRef } from "react";
import {
    NotificationsApi,
    Token,
    NotificationResponse,
    GetNotificationsResponse,
} from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import { WebsocketProviderContext } from "@/components/WebsocketProvider";

/**
 * Query keys for cache management
 */
export const NOTIFICATIONS_QUERY_KEY = "notifications";
export const NOTIFICATIONS_COUNT_QUERY_KEY = "notifications-unread-count";

/**
 * WebSocket events for notifications
 * These MUST match packages/models/src/WebsocketEvent.ts exactly
 */
const NotificationEvents = {
    NEW: "NOTIFICATION_NEW",
    READ: "NOTIFICATION_READ",
    DISMISSED: "NOTIFICATION_DISMISSED",
    COUNT_UPDATE: "NOTIFICATION_COUNT_UPDATE",
} as const;

/**
 * Fetch notifications with pagination
 */
async function fetchNotifications(
    fullToken: Token,
    limit: number,
    offset: number,
    unreadOnly?: boolean,
): Promise<GetNotificationsResponse> {
    return new NotificationsApi(getConfig(fullToken)).getNotifications({
        limit,
        offset,
        unreadOnly,
    });
}

/**
 * Fetch unread notification count
 */
async function fetchUnreadCount(fullToken: Token): Promise<number> {
    const response = await new NotificationsApi(getConfig(fullToken)).getNotificationsUnreadCount();
    return response.count;
}

interface UseNotificationsOptions {
    limit?: number;
    unreadOnly?: boolean;
    enabled?: boolean;
}

interface UseNotificationsReturn {
    notifications: NotificationResponse[];
    total: number;
    hasMore: boolean;
    isLoading: boolean;
    isFetchingMore: boolean;
    error: Error | null;
    unreadCount: number;
    isLoadingCount: boolean;
    loadMore: () => void;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    dismiss: (id: string) => Promise<void>;
    refetch: () => void;
}

/**
 * Hook for managing notifications with real-time updates
 *
 * Features:
 * - React Query for caching and background refetching
 * - WebSocket subscription for real-time notifications
 * - Optimistic updates for read/dismiss actions
 * - Pagination with loadMore
 *
 * Usage:
 *   const {
 *     notifications,
 *     unreadCount,
 *     markAsRead,
 *     markAllAsRead,
 *     loadMore,
 *   } = useNotifications();
 */
export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsReturn {
    const { limit = 20, unreadOnly = false, enabled = true } = options;

    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();
    const { socket, socketConnection } = useContext(WebsocketProviderContext);

    // Track current offset for pagination
    const offsetRef = useRef(0);

    // Main notifications query
    const {
        data: notificationsData,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useQuery({
        queryKey: [NOTIFICATIONS_QUERY_KEY, fullToken?.id, limit, unreadOnly],
        queryFn: () => fetchNotifications(fullToken!, limit, 0, unreadOnly),
        enabled: Boolean(fullToken) && enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    });

    // Unread count query (separate for badge updates)
    const {
        data: unreadCount = 0,
        isLoading: isLoadingCount,
    } = useQuery({
        queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY, fullToken?.id],
        queryFn: () => fetchUnreadCount(fullToken!),
        enabled: Boolean(fullToken) && enabled,
        staleTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: true, // Refresh count when tab becomes active
    });

    // Mark single notification as read
    const markReadMutation = useMutation({
        mutationFn: async (id: string) => {
            await new NotificationsApi(getConfig(fullToken!)).updateNotificationsIdRead({ id });
        },
        onMutate: async (id: string) => {
            // Optimistic update
            await queryClient.cancelQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });

            const previousData = queryClient.getQueryData<GetNotificationsResponse>([
                NOTIFICATIONS_QUERY_KEY,
                fullToken?.id,
                limit,
                unreadOnly,
            ]);

            if (previousData) {
                queryClient.setQueryData(
                    [NOTIFICATIONS_QUERY_KEY, fullToken?.id, limit, unreadOnly],
                    {
                        ...previousData,
                        notifications: previousData.notifications.map((n) =>
                            n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n),
                    },
                );
            }

            // Decrement unread count
            queryClient.setQueryData(
                [NOTIFICATIONS_COUNT_QUERY_KEY, fullToken?.id],
                (old: number | undefined) => Math.max(0, (old ?? 1) - 1),
            );

            return { previousData };
        },
        onError: (_err, _id, context) => {
            // Rollback on error
            if (context?.previousData) {
                queryClient.setQueryData(
                    [NOTIFICATIONS_QUERY_KEY, fullToken?.id, limit, unreadOnly],
                    context.previousData,
                );
            }
            queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY] });
        },
    });

    // Mark all as read
    const markAllReadMutation = useMutation({
        mutationFn: async () => {
            await new NotificationsApi(getConfig(fullToken!)).updateNotificationsReadAll();
        },
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });

            const previousData = queryClient.getQueryData<GetNotificationsResponse>([
                NOTIFICATIONS_QUERY_KEY,
                fullToken?.id,
                limit,
                unreadOnly,
            ]);

            if (previousData) {
                queryClient.setQueryData(
                    [NOTIFICATIONS_QUERY_KEY, fullToken?.id, limit, unreadOnly],
                    {
                        ...previousData,
                        notifications: previousData.notifications.map((n) => ({
                            ...n,
                            read: true,
                            readAt: n.readAt || new Date().toISOString(),
                        })),
                    },
                );
            }

            queryClient.setQueryData([NOTIFICATIONS_COUNT_QUERY_KEY, fullToken?.id], 0);

            return { previousData };
        },
        onError: (_err, _vars, context) => {
            if (context?.previousData) {
                queryClient.setQueryData(
                    [NOTIFICATIONS_QUERY_KEY, fullToken?.id, limit, unreadOnly],
                    context.previousData,
                );
            }
            queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY] });
        },
    });

    // Dismiss notification
    const dismissMutation = useMutation({
        mutationFn: async (id: string) => {
            await new NotificationsApi(getConfig(fullToken!)).deleteNotificationsId({ id });
        },
        onMutate: async (id: string) => {
            await queryClient.cancelQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });

            const previousData = queryClient.getQueryData<GetNotificationsResponse>([
                NOTIFICATIONS_QUERY_KEY,
                fullToken?.id,
                limit,
                unreadOnly,
            ]);

            if (previousData) {
                const notification = previousData.notifications.find((n) => n.id === id);
                const wasUnread = notification && !notification.read;

                queryClient.setQueryData(
                    [NOTIFICATIONS_QUERY_KEY, fullToken?.id, limit, unreadOnly],
                    {
                        ...previousData,
                        notifications: previousData.notifications.filter((n) => n.id !== id),
                        total: previousData.total - 1,
                    },
                );

                // If dismissed notification was unread, decrement count
                if (wasUnread) {
                    queryClient.setQueryData(
                        [NOTIFICATIONS_COUNT_QUERY_KEY, fullToken?.id],
                        (old: number | undefined) => Math.max(0, (old ?? 1) - 1),
                    );
                }
            }

            return { previousData };
        },
        onError: (_err, _id, context) => {
            if (context?.previousData) {
                queryClient.setQueryData(
                    [NOTIFICATIONS_QUERY_KEY, fullToken?.id, limit, unreadOnly],
                    context.previousData,
                );
            }
            queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY] });
        },
    });

    // Load more notifications (pagination)
    const loadMore = useCallback(async () => {
        if (!fullToken || !notificationsData || isFetching) return;

        const currentCount = notificationsData.notifications.length;
        if (currentCount >= notificationsData.total) return;

        const newOffset = currentCount;
        const moreData = await fetchNotifications(fullToken, limit, newOffset, unreadOnly);

        queryClient.setQueryData(
            [NOTIFICATIONS_QUERY_KEY, fullToken.id, limit, unreadOnly],
            {
                ...notificationsData,
                notifications: [...notificationsData.notifications, ...moreData.notifications],
            },
        );

        offsetRef.current = newOffset;
    }, [fullToken, notificationsData, isFetching, limit, unreadOnly, queryClient]);

    // WebSocket event handlers
    useEffect(() => {
        if (!socket || !socketConnection || !fullToken) return;

        // Handle new notification
        const handleNewNotification = (notification: NotificationResponse) => {
            // Add to top of list (with deduplication check)
            queryClient.setQueryData<GetNotificationsResponse>(
                [NOTIFICATIONS_QUERY_KEY, fullToken.id, limit, unreadOnly],
                (old) => {
                    if (!old) return old;
                    // Prevent duplicates - check if notification already exists
                    if (old.notifications.some((n) => n.id === notification.id)) {
                        return old;
                    }
                    return {
                        ...old,
                        notifications: [notification, ...old.notifications],
                        total: old.total + 1,
                    };
                },
            );
        };

        // Handle count update
        const handleCountUpdate = (data: { count: number }) => {
            queryClient.setQueryData([NOTIFICATIONS_COUNT_QUERY_KEY, fullToken.id], data.count);
        };

        // Handle notification read (from another device/tab)
        const handleNotificationRead = (data: { id: string }) => {
            queryClient.setQueryData<GetNotificationsResponse>(
                [NOTIFICATIONS_QUERY_KEY, fullToken.id, limit, unreadOnly],
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        notifications: old.notifications.map((n) =>
                            n.id === data.id ? { ...n, read: true } : n),
                    };
                },
            );
        };

        // Handle notification dismissed (from another device/tab)
        const handleNotificationDismissed = (data: { id: string }) => {
            queryClient.setQueryData<GetNotificationsResponse>(
                [NOTIFICATIONS_QUERY_KEY, fullToken.id, limit, unreadOnly],
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        notifications: old.notifications.filter((n) => n.id !== data.id),
                        total: old.total - 1,
                    };
                },
            );
        };

        socket.on(NotificationEvents.NEW, handleNewNotification);
        socket.on(NotificationEvents.COUNT_UPDATE, handleCountUpdate);
        socket.on(NotificationEvents.READ, handleNotificationRead);
        socket.on(NotificationEvents.DISMISSED, handleNotificationDismissed);

        return () => {
            socket.off(NotificationEvents.NEW, handleNewNotification);
            socket.off(NotificationEvents.COUNT_UPDATE, handleCountUpdate);
            socket.off(NotificationEvents.READ, handleNotificationRead);
            socket.off(NotificationEvents.DISMISSED, handleNotificationDismissed);
        };
    }, [socket, socketConnection, fullToken, queryClient, limit, unreadOnly]);

    const notifications = notificationsData?.notifications ?? [];
    const total = notificationsData?.total ?? 0;
    const hasMore = notifications.length < total;

    return {
        notifications,
        total,
        hasMore,
        isLoading,
        isFetchingMore: isFetching && notifications.length > 0,
        error: error as Error | null,
        unreadCount,
        isLoadingCount,
        loadMore,
        markAsRead: markReadMutation.mutateAsync,
        markAllAsRead: markAllReadMutation.mutateAsync,
        dismiss: dismissMutation.mutateAsync,
        refetch,
    };
}

export default useNotifications;
